import { FlagCatalogError } from './flag-catalog.error';

/**
 * The AWS AppConfig `AWS.AppConfig.FeatureFlags` flag-key constraint: a leading
 * lowercase letter followed by up to 63 more letters/digits/`_`/`-` — so a key
 * is at most 64 characters and **cannot contain `.`**. This is the AppConfig
 * schema rule the generated profile must satisfy, reproduced here as the single
 * authority both the profile generator and the AppConfig adapter encode against.
 */
export const APP_CONFIG_FLAG_KEY_PATTERN = /^[a-z][a-zA-Z\d_-]{0,63}$/;

/** The maximum length AppConfig accepts for a flag key (leading letter + 63). */
export const APP_CONFIG_FLAG_KEY_MAX_LENGTH = 64;

/**
 * The delimiter a physical key uses in place of the logical key's `.`. A logical
 * segment is forbidden from containing `_` (see {@link LOGICAL_SEGMENT_PATTERN}),
 * so the only `_` in a physical key is a delimiter the encoding substituted —
 * which is what makes the encoding injective (no two distinct logical keys map to
 * one physical key).
 */
const PHYSICAL_KEY_DELIMITER = '_';

/**
 * A single logical key segment: a leading lowercase letter followed by letters,
 * digits, and hyphens. `-` is allowed (the real catalog uses hyphenated names
 * like `routes.send-mail`, `wiring.project-store`), and AppConfig's physical-key
 * pattern permits `-`, so a hyphen survives the encoding unchanged. `_` is
 * deliberately **forbidden inside a segment** so that the only `_` in a physical
 * key is the delimiter {@link toPhysicalKey} substitutes for `.`. This is what
 * keeps the logical→physical encoding injective: `wiring.a.b` → `wiring_a_b`
 * cannot collide with any other logical key, because `wiring.a_b` (which would
 * also encode to `wiring_a_b`) is not a legal logical key — its second segment
 * contains a `_`.
 */
const LOGICAL_SEGMENT_PATTERN = /^[a-z][a-zA-Z\d-]*$/;

/**
 * Encode a logical catalog flag key (`routes.foo`, `wiring.projectStore`,
 * `ui.theme`) into its physical AWS AppConfig flag key.
 *
 * The dotted logical prefix is a **catalog-level identifier** that names the read
 * mechanism; it is NOT a legal AppConfig flag key, because the AppConfig schema
 * forbids `.`. ADR-0016 fixes a single **injective** logical→physical encoding
 * used in ONE direction by both the profile generator (which starts from the
 * catalog's logical keys and emits AppConfig-legal physical keys) and the
 * AppConfig adapter (which encodes a logical lookup key to its physical form
 * before fetching), so there is never a physical→logical decode to keep in sync.
 *
 * The encoding substitutes `.` with `_`. Because every logical segment is
 * `[a-z][a-zA-Z0-9-]*` (no `_` inside a segment), the only `_` in the result is a
 * substituted delimiter, so the mapping is injective.
 *
 * Build-time validation (this throws rather than emit a profile AppConfig would
 * refuse):
 *
 * - every segment matches {@link LOGICAL_SEGMENT_PATTERN} (a legal logical key);
 * - the encoded physical key still matches {@link APP_CONFIG_FLAG_KEY_PATTERN} —
 *   in particular a legal logical key can still encode to an **overlong** (>64
 *   char) physical key, which AppConfig refuses, so the length bound is enforced
 *   here too.
 *
 * @param logical the fully-qualified logical catalog key (`${prefix}.${name}`).
 * @returns the AppConfig-legal physical flag key.
 * @throws FlagCatalogError when the logical key is malformed or encodes to a key
 *   AppConfig would reject.
 */
export function toPhysicalKey(logical: string): string {
    const segments = logical.split('.');

    if (segments.length < 2) {
        throw new FlagCatalogError(
            `Logical flag key '${logical}' is not a qualified '<prefix>.<name>' key: ` +
                `a catalog key has a mechanism prefix and at least one name segment.`
        );
    }

    for (const segment of segments) {
        if (!LOGICAL_SEGMENT_PATTERN.test(segment)) {
            throw new FlagCatalogError(
                `Logical flag key '${logical}' has an illegal segment '${segment}'. ` +
                    `Each segment must match ${LOGICAL_SEGMENT_PATTERN} ` +
                    `(a leading lowercase letter then letters/digits/'-' only; '_' is forbidden inside a segment ` +
                    `so the only '_' in a physical key is the delimiter).`
            );
        }
    }

    const physical = segments.join(PHYSICAL_KEY_DELIMITER);

    if (!APP_CONFIG_FLAG_KEY_PATTERN.test(physical)) {
        throw new FlagCatalogError(
            `Logical flag key '${logical}' encodes to physical key '${physical}' (${physical.length} chars), ` +
                `which AWS AppConfig would reject: a flag key must match ${APP_CONFIG_FLAG_KEY_PATTERN} ` +
                `(at most ${APP_CONFIG_FLAG_KEY_MAX_LENGTH} characters).`
        );
    }

    return physical;
}
